import { nextTick, onBeforeUnmount, onMounted, Ref, ref, TemplateRef, watch } from "vue";
import findNearestCommonParent from "@logic/dom/FindNearestCommonParent";
import { findElemToDrag, getElemWithSizeAndCoordList, createGhost, setGhostSamePositionAsCursor, detectIntersectionsThrottled, checkIsScrollingNeededThrottled } from "./logic";
import { MOUSE_MOVE_SENSITIVITY_THRESHOLD_PX, TOUCH_MOVE_SENSITIVITY_THRESHOLD_PX, TOUCH_DRAG_START_TIMEOUT_MS, SET_ELEM_WITH_SIZE_AND_COORD_LIST_DEBOUNCE_MS } from "./constants";
import { CoordinatesXY, ElemWithSizeAndCoord, DnDItemData } from "./types";
import lodashDebounce from "lodash-es/debounce";

/**
 *
 * @param isEnabled
 * @param elemsInvolvedInDnD All the template refs to calc intersection with
 * @param dragEndCallback
 * @param ghostShift Defines the drag&drop ghost position relative to the cursor
 * @param ghostOpacity Opacity of the dragged element that follows the cursor
 */
export function useDragNDrop(
  isEnabled: Ref<boolean>,
  elemsInvolvedInDnD: Ref<Array<TemplateRef>>,
  dragEndCallback: Function,
  ghostShift?: CoordinatesXY = { x: 40, y: 40 },
  ghostOpacity? = "0.9"
): {
  isDragInProgress: Ref<boolean>;
  draggedItemData: Ref<DnDItemData | null>;
  intersectedItemDataArray: Ref<Array<DnDItemData>>;
} {
  let elemsInvolvedInDnDWithSizeAndCoord: Array<ElemWithSizeAndCoord> = [];
  let commonParent: Element | null = null;
  let ghostNode: Element | null = null;

  const draggedItemData = ref<DnDItemData | null>(null);
  const intersectedItemDataArray = ref<Array<DnDItemData>>([]);

  const isDragInProgress = ref<boolean>(false);
  let cursorStartCoordinates = { x: 0, y: 0 };

  // Touch drag event should start with a slight delay to avoid false drag activations while scrolling the page (because unlike "mouse-based" devices the touch devices require constant touches to scroll)
  let touchStartTimeStamp = 0;

  // Adds listeners ===========================================================
  function addListeners() {
    if (!isEnabled.value) {
      return;
    }

    if (!elemsInvolvedInDnD.value?.length) {
      throw new Error(`useDragNDrop: elemsInvolvedInDnD is ${elemsInvolvedInDnD.value?.length === 0 ? "empty" : elemsInvolvedInDnD}`);
    }
    commonParent = findNearestCommonParent(elemsInvolvedInDnD.value);

    addMouseListeners(commonParent);
    addTouchListeners(commonParent);
  }

  onMounted(async () => {
    if (isEnabled.value) {
      await nextTick();
      addListeners();
    }
  });

  watch(isEnabled, async () => {
    if (isEnabled.value) {
      await nextTick();
      addListeners();
    }
  });

  // Adds mouse listeners
  function addMouseListeners() {
    commonParent.addEventListener("mousedown", handleMouseDown);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  }

  // Adds touch listeners
  function addTouchListeners() {
    commonParent.addEventListener("touchstart", handleTouchStart);

    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);
  }

  // Sets all involved elems size and coord ===================================
  const setElemWithSizeAndCoordListDebounced = lodashDebounce(setElemWithSizeAndCoordList, SET_ELEM_WITH_SIZE_AND_COORD_LIST_DEBOUNCE_MS);

  function setElemWithSizeAndCoordList() {
    elemsInvolvedInDnDWithSizeAndCoord = getElemWithSizeAndCoordList(elemsInvolvedInDnD);
  }

  // Removes listeners ========================================================
  function removeListeners() {
    removeMouseListeners();
    removeTouchListeners();
  }

  onBeforeUnmount(removeListeners);

  watch(isEnabled, () => {
    if (!isEnabled.value) {
      removeListeners();
    }
  });

  // Removes mouse listeners
  function removeMouseListeners() {
    commonParent?.removeEventListener("mousedown", handleMouseDown);

    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  }

  // Removes touch listeners
  function removeTouchListeners() {
    commonParent?.removeEventListener("touchstart", handleTouchStart);

    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  }

  // Handles cursor start =====================================================
  function handleCursorStart(eventTarget: HTMLElement, cursorCoord: CoordinatesXY) {
    if (!isEnabled.value) {
      removeListeners();
      return;
    }

    cursorStartCoordinates = { x: cursorCoord.x, y: cursorCoord.y };

    const elementToDrag = findElemToDrag(eventTarget, elemsInvolvedInDnD, commonParent);

    if (!elementToDrag) {
      resetLastDnDSessionValues();
      return;
    }

    draggedItemData.value = { dataset: elementToDrag.dataset, domNode: elementToDrag };
    ghostNode = createGhost(elementToDrag, ghostOpacity);
    setGhostSamePositionAsCursor(ghostNode, { x: cursorCoord.x, y: cursorCoord.y } as CoordinatesXY, ghostShift);

    setElemWithSizeAndCoordList();
  }

  // Handles mouse down
  function handleMouseDown(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    isDragInProgress.value = true;
    handleCursorStart(event.target, { x: event.x, y: event.y });
  }

  // Handles touch start
  async function handleTouchStart(event: TouchEvent) {
    event.preventDefault();

    // Touch drag event should start with a slight delay to avoid false drag activations while scrolling the page (because unlike "mouse-based" devices the touch devices require constant touches to scroll)
    touchStartTimeStamp = Date.now();
    await new Promise(resolve => setTimeout(resolve, TOUCH_DRAG_START_TIMEOUT_MS));

    // continue with the drag&drop only if the timeout passed and the user didn't release the touch (timestap nulled out on the touchEnd event)
    // (which means that it was not a false activation)
    if (touchStartTimeStamp === 0) {
      return;
    }

    isDragInProgress.value = true;
    handleCursorStart(event.target, { x: event.touches[0].clientX, y: event.touches[0].clientY });
  }

  // Handles cursor move ======================================================
  function handleCursorMove(cursorCoord: CoordinatesXY, sensitivityThreshold: number) {
    if (!isEnabled.value || !isDragInProgress.value) {
      return;
    }

    const isScrollHappened = checkIsScrollingNeededThrottled(cursorCoord);
    // All element coordinates should be recalculated because they depend on the window scroll position
    if (isScrollHappened) {
      setElemWithSizeAndCoordListDebounced();
    }

    const moveByXAbs = Math.abs(Math.abs(cursorStartCoordinates.x) - Math.abs(cursorCoord.x));
    const moveByYAbs = Math.abs(Math.abs(cursorStartCoordinates.y) - Math.abs(cursorCoord.y));

    if (moveByXAbs <= sensitivityThreshold && moveByYAbs <= sensitivityThreshold) {
      // mouseMove event on mobile is very sensitive, and it detects even the smallest fraction of pixel which we don't need
      return;
    }

    setGhostSamePositionAsCursor(ghostNode, { x: cursorCoord.x, y: cursorCoord.y } as CoordinatesXY, ghostShift);
    intersectedItemDataArray.value = detectIntersectionsThrottled(elemsInvolvedInDnDWithSizeAndCoord, { x: cursorCoord.x, y: cursorCoord.y } as CoordinatesXY);
  }

  // Handles mouse move
  function handleMouseMove(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    handleCursorMove({ x: event.x, y: event.y }, MOUSE_MOVE_SENSITIVITY_THRESHOLD_PX);
  }

  // Handles touch move
  function handleTouchMove(event: TouchEvent) {
    handleCursorMove({ x: event.touches[0].clientX, y: event.touches[0].clientY }, TOUCH_MOVE_SENSITIVITY_THRESHOLD_PX);
  }

  // Handles cursor up ========================================================
  function handleCursorUp() {
    if (!isDragInProgress.value) {
      resetLastDnDSessionValues();
      return;
    }

    dragEndCallback(draggedItemData, intersectedItemDataArray);
    resetLastDnDSessionValues();
  }

  // Handles mouse up
  function handleMouseUp() {
    handleCursorUp();
  }

  // Handles touch end
  function handleTouchEnd() {
    handleCursorUp();
  }

  // Resets all values ========================================================
  function resetLastDnDSessionValues() {
    ghostNode?.remove();
    ghostNode = null;

    isDragInProgress.value = false;
    touchStartTimeStamp = 0;

    cursorStartCoordinates = { x: 0, y: 0 };

    draggedItemData.value = null;
    intersectedItemDataArray.value = [];
  }

  // Check is dragged item equals to an id ====================================
  function matchWithDraggedId(id: string): boolean {
    return draggedItemData.value?.dataset.id === id;
  }

  // Finds an intersected element by id from the dataset ======================
  /**
   * Finds intersection by id from the element dataset
   * @param id
   */
  function findIntersectedById(id: string): DnDItemData | null {
    return intersectedItemDataArray.value.find(data => data.dataset.id === id) || null;
  }

  return {
    isDragInProgress,
    draggedItemData,
    intersectedItemDataArray,

    matchWithDraggedId,
    findIntersectedById,
  };
}
